const SLIDER_DEFAULTS = {
  accessibility: true,
  adaptiveHeight: false,
  appendArrows: null,
  appendDots: null,
  arrows: true,
  asNavFor: null,
  autoplay: false,
  autoplaySpeed: 3000,
  centerMode: false,
  centerPadding: '50px',
  cssEase: 'ease',
  customPaging: null,
  dots: false,
  dotsClass: 'slick-dots',
  draggable: true,
  easing: 'linear',
  edgeFriction: 0.35,
  fade: false,
  focusOnSelect: false,
  focusOnChange: false,
  infinite: true,
  initialSlide: 0,
  lazyLoad: 'ondemand',
  mobileFirst: false,
  nextArrow: '<button class="slick-next" aria-label="Next" type="button">Next</button>',
  pauseOnDotsHover: false,
  pauseOnFocus: true,
  pauseOnHover: true,
  prevArrow: '<button class="slick-prev" aria-label="Previous" type="button">Previous</button>',
  respondTo: 'window',
  responsive: null,
  rows: 1,
  rtl: false,
  slide: '',
  slidesPerRow: 1,
  slidesToScroll: 1,
  slidesToShow: 1,
  speed: 500,
  swipe: true,
  swipeToSlide: false,
  touchMove: true,
  touchThreshold: 5,
  useCSS: true,
  useTransform: true,
  variableWidth: false,
  vertical: false,
  verticalSwiping: false,
  waitForAnimate: true,
  zIndex: 1000,
};

let instanceUid = 0;

const toNodes = (target) => {
  if (!target) return [];
  if (typeof target === 'string') return [...document.querySelectorAll(target)];
  if (target instanceof Element) return [target];
  return [...target].filter((node) => node instanceof Element);
};

const fromMarkup = (markup) => {
  const holder = document.createElement('div');
  holder.innerHTML = String(markup).trim();
  return holder.firstElementChild;
};

class CometSlider {
  constructor(element, options = {}) {
    this.el = element;
    this.instanceUid = instanceUid++;
    this.originalSettings = { ...SLIDER_DEFAULTS, ...options };
    this.options = { ...this.originalSettings };
    this.originalHTML = element.innerHTML;

    this.currentSlide = this.options.initialSlide ?? 0;
    this.slideCount = 0;
    this.slideWidth = 0;
    this.slideHeight = 0;
    this.unslicked = false;
    this.animating = false;
    this.interrupted = false;
    this.dragged = false;
    this.activeBreakpoint = null;
    this.autoplayTimer = null;
    this.touch = null;
    this.navGroup = [];

    for (const name of [
      'onResize', 'onDragStart', 'onDragMove', 'onDragEnd',
      'onClickCapture', 'onKeyDown', 'onSlideClick', 'onDragBlock',
    ]) this[name] = this[name].bind(this);

    this.registerBreakpoints();
    this.checkResponsive(true);
    if (!this.unslicked) this.init();

    window.addEventListener('resize', this.onResize);
    this.observeSize();
    element.slick = this;
    CometSlider.instances.add(this);
    this.linkNav();
  }

  /* ---------- responsive ---------- */

  registerBreakpoints() {
    this.breakpoints = [];
    this.breakpointSettings = {};
    const { responsive, mobileFirst } = this.originalSettings;
    if (!Array.isArray(responsive)) return;

    for (const entry of responsive) {
      this.breakpoints.push(entry.breakpoint);
      this.breakpointSettings[entry.breakpoint] = entry.settings;
    }
    this.breakpoints.sort((a, b) => (mobileFirst ? a - b : b - a));
  }

  respondWidth() {
    const { respondTo } = this.options;
    const win = window.innerWidth;
    const slider = this.el.offsetWidth;
    if (respondTo === 'slider') return slider;
    if (respondTo === 'min') return Math.min(win, slider);
    return win;
  }

  // Slick: mobileFirst=false -> `width < breakpoint`; mobileFirst=true -> `width > breakpoint`.
  // Strict comparison both ways: breakpoint 990 applies at 989, not at 990.
  checkResponsive(initial = false) {
    if (!this.breakpoints.length) return false;

    const width = this.respondWidth();
    const { mobileFirst } = this.originalSettings;
    let target = null;

    for (const breakpoint of this.breakpoints) {
      if (mobileFirst ? width > breakpoint : width < breakpoint) target = breakpoint;
    }

    if (target === this.activeBreakpoint) return false;
    this.activeBreakpoint = target;

    const settings = target === null ? null : this.breakpointSettings[target];

    if (settings === 'unslick') {
      if (!this.unslicked) this.unslick(target);
      return true;
    }

    this.options = target === null
      ? { ...this.originalSettings }
      : { ...this.originalSettings, ...settings };

    this.emit('breakpoint', { breakpoint: target });

    if (this.unslicked) {
      this.unslicked = false;
      this.currentSlide = this.options.initialSlide ?? 0;
      this.init();
      return true;
    }

    if (!initial) this.refresh();
    return !initial;
  }

  /* ---------- build ---------- */

  init() {
    this.buildRows();
    this.buildOut();
    this.buildArrows();
    this.buildDots();
    this.setSlideClasses(this.currentSlide);
    this.setPosition();
    this.bindEvents();
    this.initADA();
    this.lazyLoad();
    this.startAutoplay();

    this.el.classList.add('slick-initialized');
    this.emit('init', { currentSlide: this.currentSlide });
    this.emit('reInit', { currentSlide: this.currentSlide });
  }

  // rows > 1 regroups children into row/cell wrappers before slides are read.
  buildRows() {
    const { rows, slidesPerRow, slide } = this.options;
    if (rows <= 1 && slidesPerRow <= 1) return;

    const children = [...this.el.children];
    const perSlide = rows * slidesPerRow;
    const slideTotal = Math.ceil(children.length / perSlide);
    const frag = document.createDocumentFragment();
    let cursor = 0;

    for (let s = 0; s < slideTotal; s += 1) {
      const slideEl = document.createElement('div');
      for (let r = 0; r < rows; r += 1) {
        const row = document.createElement('div');
        for (let c = 0; c < slidesPerRow; c += 1) {
          const cell = document.createElement('div');
          cell.style.width = `${100 / slidesPerRow}%`;
          cell.style.display = 'inline-block';
          if (children[cursor]) cell.appendChild(children[cursor]);
          cursor += 1;
          row.appendChild(cell);
        }
        slideEl.appendChild(row);
      }
      frag.appendChild(slideEl);
    }

    this.el.innerHTML = '';
    this.el.appendChild(frag);
    this.rowsApplied = true;
    if (slide) this.options.slide = '';
  }

  buildOut() {
    const { slide, draggable, vertical, rtl } = this.options;
    const all = [...this.el.children].filter((n) => !n.classList.contains('slick-cloned'));
    const children = slide ? all.filter((n) => n.matches(slide)) : all;

    this.slides = children;
    this.slideCount = children.length;

    this.el.classList.add('slick-slider');
    if (vertical) this.el.classList.add('slick-vertical');
    if (rtl) this.el.setAttribute('dir', 'rtl');

    this.track = document.createElement('div');
    this.track.className = 'slick-track';

    this.list = document.createElement('div');
    this.list.className = 'slick-list';
    if (draggable) this.list.classList.add('draggable');

    for (const node of children) {
      node.classList.add('slick-slide');
      this.track.appendChild(node);
    }

    this.list.appendChild(this.track);
    this.el.insertBefore(this.list, this.el.firstChild);

    this.setupInfinite();
    this.indexSlides();
  }

  // Mirrors Slick exactly: o = centerMode ? slidesToShow + 1 : slidesToShow;
  // prepend o clones, append (o + slideCount) clones.
  setupInfinite() {
    this.clonesBefore = [];
    this.clonesAfter = [];
    const { infinite, fade, centerMode, slidesToShow } = this.options;
    if (!infinite || fade || this.slideCount <= slidesToShow) return;

    const o = centerMode ? slidesToShow + 1 : slidesToShow;

    for (let i = this.slideCount; i > this.slideCount - o; i -= 1) {
      const index = i - 1;
      const source = this.slides[index];
      if (!source) continue;
      const clone = source.cloneNode(true);
      clone.classList.add('slick-cloned');
      clone.removeAttribute('id');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-slick-index', String(index - this.slideCount));
      this.track.insertBefore(clone, this.track.firstChild);
      this.clonesBefore.unshift(clone);
    }

    // Slick indexes $slides[i] without wrapping; i >= slideCount yields an empty
    // jQuery set and clones nothing. Skipping reproduces that exactly.
    for (let i = 0; i < o + this.slideCount; i += 1) {
      const source = this.slides[i];
      if (!source) continue;
      const clone = source.cloneNode(true);
      clone.classList.add('slick-cloned');
      clone.removeAttribute('id');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-slick-index', String(i + this.slideCount));
      this.track.appendChild(clone);
      this.clonesAfter.push(clone);
    }
  }

  // Clones carry negative / overflowing indexes so `n ± slideCount` lookups resolve.
  indexSlides() {
    this.slides.forEach((node, i) => node.setAttribute('data-slick-index', String(i)));
  }

  hasNav() {
    return this.slideCount > this.options.slidesToShow;
  }

  buildArrows() {
    const { arrows, prevArrow, nextArrow, appendArrows } = this.options;
    if (!arrows || !this.hasNav()) return;

    const host = toNodes(appendArrows)[0] ?? this.el;
    this.prevArrow = this.mountArrow(prevArrow, host, () => this.slidePrev());
    this.nextArrow = this.mountArrow(nextArrow, host, () => this.slideNext());
    this.updateArrows();
  }

  mountArrow(markup, host, handler) {
    const button = markup instanceof Element ? markup : fromMarkup(markup);
    if (!button) return null;
    button.classList.add('slick-arrow');
    button.addEventListener('click', (event) => {
      event.preventDefault();
      this.interrupted = true;
      handler();
    });
    host.appendChild(button);
    return button;
  }

  buildDots() {
    const { dots, dotsClass, appendDots, customPaging } = this.options;
    if (!dots || !this.hasNav()) return;

    const pageCount = this.dotCount();
    this.dots = document.createElement('ul');
    this.dots.className = dotsClass;
    this.dots.setAttribute('role', 'tablist');

    for (let i = 0; i < pageCount; i += 1) {
      const item = document.createElement('li');
      const inner = customPaging ? customPaging(this, i) : null;

      let button;
      if (inner instanceof Element) button = inner;
      else if (typeof inner === 'string') button = fromMarkup(inner);
      else {
        button = document.createElement('button');
        button.textContent = String(i + 1);
      }

      if (button.tagName === 'BUTTON') button.type = 'button';
      button.setAttribute('role', 'tab');
      // login-account.liquid keys off this exact "N of M" string.
      button.setAttribute('aria-label', `${i + 1} of ${pageCount}`);
      button.setAttribute('id', `slick-slide-control${this.instanceUid}${i}`);
      item.appendChild(button);

      item.addEventListener('click', (event) => {
        event.preventDefault();
        this.interrupted = true;
        this.goTo(i * this.options.slidesToScroll);
      });
      this.dots.appendChild(item);
    }

    (toNodes(appendDots)[0] ?? this.el).appendChild(this.dots);
    this.el.classList.add('slick-dotted');
    this.updateDots();
  }

  // Ported from Slick's getDotCount(); note it returns one LESS than the dot
  // count, and buildDots loops `e <= getDotCount()`.
  getDotCount() {
    const { infinite, centerMode, slidesToShow, slidesToScroll } = this.options;
    let breakPoint = 0;
    let counter = 0;
    let pagerQty = 0;

    if (infinite) {
      if (this.slideCount <= slidesToShow) {
        pagerQty += 1;
      } else {
        while (breakPoint < this.slideCount) {
          pagerQty += 1;
          breakPoint = counter + slidesToScroll;
          counter += slidesToScroll <= slidesToShow ? slidesToScroll : slidesToShow;
        }
      }
    } else if (centerMode) {
      pagerQty = this.slideCount;
    } else {
      pagerQty = 1 + Math.ceil((this.slideCount - slidesToShow) / slidesToScroll);
    }

    return pagerQty - 1;
  }

  dotCount() {
    return this.getDotCount() + 1;
  }

  /* ---------- geometry ---------- */

  // Slick measures with jQuery .width()/.height() — the CONTENT box.
  // clientWidth includes padding, which centerPadding sets, so subtract it back.
  listContentSize() {
    const style = getComputedStyle(this.list);
    return {
      width: this.list.clientWidth
        - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0),
      height: this.list.clientHeight
        - parseFloat(style.paddingTop || 0) - parseFloat(style.paddingBottom || 0),
    };
  }

  setDimensions() {
    const { vertical, centerMode, centerPadding, slidesToShow, variableWidth, fade } = this.options;
    const trackChildren = [...this.track.children];

    this.list.style.padding = centerMode
      ? (vertical ? `${centerPadding} 0px` : `0px ${centerPadding}`)
      : '';

    if (vertical) {
      const first = this.slides[0];
      if (first) this.list.style.height = `${first.offsetHeight * slidesToShow}px`;
    }

    const { width: listWidth, height: listHeight } = this.listContentSize();
    this.listWidth = listWidth;
    this.listHeight = listHeight;

    if (fade) {
      this.slideWidth = Math.ceil(listWidth);
      const fadeExtra = trackChildren[0] ? this.slideExtra(trackChildren[0]) : null;
      for (const node of trackChildren) this.applyWidth(node, this.slideWidth, fadeExtra);
      this.track.style.width = '100%';
      return;
    }

    if (variableWidth) {
      this.track.style.width = `${5000 * this.slideCount}px`;
      for (const node of trackChildren) {
        if (node.dataset.cometWidth !== '1') continue;
        node.style.width = '';
        delete node.dataset.cometWidth;
      }
      return;
    }

    if (vertical) {
      this.slideHeight = Math.ceil(listHeight / slidesToShow);
      for (const node of trackChildren) node.style.height = `${this.slideHeight}px`;
      this.track.style.height = `${this.slideHeight * trackChildren.length}px`;
      return;
    }

    this.slideWidth = Math.ceil(listWidth / slidesToShow);
    const extra = trackChildren[0] ? this.slideExtra(trackChildren[0]) : null;
    for (const node of trackChildren) this.applyWidth(node, this.slideWidth, extra);
    this.track.style.width = `${this.slideWidth * trackChildren.length}px`;
  }

  // Slick: `slideWidth - (outerWidth(true) - width())` via jQuery .width(),
  // i.e. subtract margin+padding+border so the slide OCCUPIES exactly slideWidth,
  // then set it as a content-box value honouring box-sizing.
  slideExtra(node) {
    const cs = getComputedStyle(node);
    const pad = parseFloat(cs.paddingLeft || 0) + parseFloat(cs.paddingRight || 0);
    const bor = parseFloat(cs.borderLeftWidth || 0) + parseFloat(cs.borderRightWidth || 0);
    const mar = parseFloat(cs.marginLeft || 0) + parseFloat(cs.marginRight || 0);
    return { pad, bor, mar, total: pad + bor + mar, borderBox: cs.boxSizing === 'border-box' };
  }

  applyWidth(node, px, extra) {
    const e = extra ?? this.slideExtra(node);
    const content = Math.max(px - e.total, 0);
    node.style.width = `${e.borderBox ? content + e.pad + e.bor : content}px`;
    node.dataset.cometWidth = '1';
  }

  trackChildAt(index) {
    return this.track.children[index + this.clonesBefore.length] ?? null;
  }

  // Ported from Slick's getLeft(). The offset derives from slidesToShow (which
  // may be fractional), NOT the clone count, and centerMode centres via
  // slideWidth * floor(slidesToShow / 2).
  getLeft(index) {
    const {
      infinite, centerMode, variableWidth, vertical, rtl,
      slidesToShow, slidesToScroll,
    } = this.options;

    const unit = vertical ? this.slideHeight : this.slideWidth;
    let slideOffset = 0;

    if (infinite) {
      if (this.slideCount > slidesToShow) slideOffset = unit * slidesToShow * -1;
      if (this.slideCount % slidesToScroll !== 0
          && index + slidesToScroll > this.slideCount
          && this.slideCount > slidesToShow) {
        slideOffset = index > this.slideCount
          ? (slidesToShow - (index - this.slideCount)) * unit * -1
          : (this.slideCount % slidesToScroll) * unit * -1;
      }
    } else if (index + slidesToShow > this.slideCount) {
      slideOffset = (index + slidesToShow - this.slideCount) * unit;
    }

    if (this.slideCount <= slidesToShow) slideOffset = 0;

    if (centerMode && this.slideCount <= slidesToShow) {
      slideOffset = (unit * Math.floor(slidesToShow)) / 2 - (unit * this.slideCount) / 2;
    } else if (centerMode && infinite) {
      slideOffset += unit * Math.floor(slidesToShow / 2) - unit;
    } else if (centerMode) {
      slideOffset = unit * Math.floor(slidesToShow / 2);
    }

    let left = index * unit * -1 + slideOffset;

    if (variableWidth && !vertical) {
      const target = (this.slideCount <= slidesToShow || !infinite)
        ? this.track.children[index]
        : this.track.children[index + Math.ceil(slidesToShow) + (centerMode ? 1 : 0)];
      if (!target) return 0;
      left = rtl
        ? -1 * (this.track.offsetWidth - target.offsetLeft - target.offsetWidth)
        : -1 * target.offsetLeft;
      if (centerMode) left += Math.floor((this.listWidth - target.offsetWidth) / 2);
    }

    return left;
  }

  setCSS(position, animate = false) {
    const { vertical, speed, cssEase, useCSS, useTransform } = this.options;

    if (!useCSS || !useTransform) {
      this.animateFallback(position, animate);
      return;
    }

    // Slick rounds with Math.ceil() before writing the transform.
    const px = Math.ceil(position);
    this.track.style.transition = animate ? `transform ${speed}ms ${cssEase}` : '';
    this.track.style.transform = vertical
      ? `translate3d(0, ${px}px, 0)`
      : `translate3d(${px}px, 0, 0)`;
  }

  animateFallback(position, animate) {
    const prop = this.options.vertical ? 'top' : 'left';
    this.track.style.position = 'relative';
    this.track.style.transition = '';
    this.track.style.transform = '';

    if (!animate) {
      this.track.style[prop] = `${Math.ceil(position)}px`;
      return;
    }

    const from = parseFloat(this.track.style[prop] || 0);
    const start = performance.now();
    const { speed } = this.options;

    const step = (now) => {
      const t = Math.min((now - start) / speed, 1);
      this.track.style[prop] = `${from + (position - from) * t}px`;
      if (t < 1) this.rafId = requestAnimationFrame(step);
    };
    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(step);
  }

  setFade(index, animate = false) {
    const { speed, cssEase, zIndex } = this.options;
    this.track.style.transform = '';
    this.track.style.transition = '';

    this.slides.forEach((node, i) => {
      const active = i === index;
      node.style.position = 'relative';
      node.style.left = `${-i * this.slideWidth}px`;
      node.style.zIndex = active ? zIndex : zIndex - 2;
      node.style.transition = animate ? `opacity ${speed}ms ${cssEase}` : '';
      node.style.opacity = active ? '1' : '0';
    });
  }

  setHeight() {
    if (!this.options.adaptiveHeight || this.options.vertical) return;
    const active = this.slides[this.currentSlide];
    if (active) this.list.style.height = `${active.offsetHeight}px`;
  }

  setPosition() {
    if (this.unslicked) return;
    this.setDimensions();
    this.setHeight();
    if (this.options.fade) this.setFade(this.currentSlide);
    else this.setCSS(this.getLeft(this.currentSlide));
    this.emit('setPosition', { currentSlide: this.currentSlide });
  }

  /* ---------- state classes ---------- */

  normalize(index) {
    return ((index % this.slideCount) + this.slideCount) % this.slideCount;
  }

  setSlideClasses(index) {
    const { centerMode, slidesToShow } = this.options;

    for (const node of this.track.children) {
      node.classList.remove('slick-active', 'slick-center', 'slick-current');
      node.setAttribute('aria-hidden', 'true');
    }

    const current = this.slides[index];
    current?.classList.add('slick-current');

    const mark = (node) => {
      node?.classList.add('slick-active');
      node?.setAttribute('aria-hidden', 'false');
    };

    if (centerMode) {
      const half = Math.floor(slidesToShow / 2);
      for (let i = index - half; i <= index + half; i += 1) {
        mark(this.slides[this.normalize(i)]);
      }
      current?.classList.add('slick-center');
      return;
    }

    const span = Math.floor(slidesToShow);
    for (let i = index; i < index + span; i += 1) {
      mark(this.options.infinite ? this.slides[this.normalize(i)] : this.slides[i]);
    }
  }

  // Ported from Slick's updateArrows(): bounds use the RAW (possibly
  // fractional) slidesToShow, not a rounded value.
  updateArrows() {
    const { arrows, infinite, centerMode, slidesToShow } = this.options;
    if (!this.prevArrow && !this.nextArrow) return;
    if (!arrows || this.slideCount <= slidesToShow || infinite) {
      this.setArrow(this.prevArrow, false);
      this.setArrow(this.nextArrow, false);
      return;
    }

    if (this.currentSlide === 0) {
      this.setArrow(this.prevArrow, true);
      this.setArrow(this.nextArrow, false);
    } else if (this.currentSlide >= this.slideCount - slidesToShow && !centerMode) {
      this.setArrow(this.nextArrow, true);
      this.setArrow(this.prevArrow, false);
    } else if (this.currentSlide >= this.slideCount - 1 && centerMode) {
      this.setArrow(this.nextArrow, true);
      this.setArrow(this.prevArrow, false);
    } else {
      this.setArrow(this.prevArrow, false);
      this.setArrow(this.nextArrow, false);
    }
  }

  setArrow(arrow, disabled) {
    if (!arrow) return;
    arrow.classList.toggle('slick-disabled', disabled);
    arrow.setAttribute('aria-disabled', String(disabled));
  }

  updateDots() {
    if (!this.dots) return;
    const active = Math.floor(this.currentSlide / this.options.slidesToScroll);
    [...this.dots.children].forEach((item, i) => {
      item.classList.toggle('slick-active', i === active);
      item.querySelector('button')?.setAttribute('aria-selected', String(i === active));
    });
  }

  /* ---------- navigation ---------- */

  slideNext() { this.goTo(this.currentSlide + this.options.slidesToScroll); }

  slidePrev() { this.goTo(this.currentSlide - this.options.slidesToScroll); }

  goTo(target, animate = true, fromNav = false) {
    if (this.unslicked) return;
    if (this.animating && this.options.waitForAnimate) return;

    const { infinite, centerMode, fade, speed, slidesToScroll, focusOnChange } = this.options;

    // Slick's slideHandler bounds: out of range means refuse the move and
    // settle back on the current slide, not clamp to the last one.
    if (!infinite) {
      const ceiling = centerMode
        ? this.slideCount - slidesToScroll
        : this.getDotCount() * slidesToScroll;
      if (target < 0 || target > ceiling) {
        this.emit('edge', { direction: target < 0 ? 'left' : 'right' });
        if (!fade) this.setCSS(this.getLeft(this.currentSlide), animate);
        return;
      }
    }

    const raw = target;
    const next = infinite ? this.normalize(raw) : raw;
    if (!infinite && next === this.currentSlide && !fromNav) return;

    this.emit('beforeChange', { currentSlide: this.currentSlide, nextSlide: next });

    const wrapped = infinite && raw !== next && !fade;
    this.currentSlide = next;

    if (fade) this.setFade(next, animate);
    else this.setCSS(this.getLeft(wrapped ? raw : next), animate);

    this.setSlideClasses(next);
    this.setHeight();
    this.updateArrows();
    this.updateDots();
    this.lazyLoad();
    if (!fromNav) this.syncNav(next);

    if (focusOnChange) this.slides[next]?.focus?.();

    if (!animate) return;

    this.animating = true;
    window.clearTimeout(this.settleTimer);
    this.settleTimer = window.setTimeout(() => {
      this.animating = false;
      if (wrapped) this.setCSS(this.getLeft(next), false);
      this.emit('afterChange', { currentSlide: this.currentSlide });
    }, speed);
  }

  /* ---------- asNavFor ---------- */

  linkNav() {
    const targets = toNodes(this.options.asNavFor);
    this.navGroup = targets.map((node) => node.slick).filter(Boolean);
  }

  syncNav(index) {
    this.linkNav();
    for (const other of this.navGroup) {
      if (other && other !== this && other.currentSlide !== index) {
        other.goTo(index, true, true);
      }
    }
  }

  /* ---------- lazy load ---------- */

  lazyLoad() {
    const { lazyLoad, slidesToShow } = this.options;
    if (lazyLoad !== 'ondemand' && lazyLoad !== 'progressive') return;

    const load = (scope) => {
      for (const img of scope.querySelectorAll('img[data-lazy]:not([src])')) {
        img.addEventListener('load', () => {
          img.classList.remove('slick-loading');
          this.emit('lazyLoaded', { image: img });
        }, { once: true });
        img.addEventListener('error', () => this.emit('lazyLoadError', { image: img }), { once: true });
        img.src = img.dataset.lazy;
      }
    };

    if (lazyLoad === 'progressive') {
      load(this.track);
      return;
    }

    const span = Math.ceil(slidesToShow) + 1;
    for (let i = this.currentSlide; i < this.currentSlide + span; i += 1) {
      const node = this.slides[this.normalize(i)];
      if (node) load(node);
    }
  }

  /* ---------- events / input ---------- */

  bindEvents() {
    const { swipe, draggable, touchMove, accessibility, focusOnSelect } = this.options;
    if ((swipe || draggable) && touchMove) {
      this.list.addEventListener('pointerdown', this.onDragStart);
      this.list.addEventListener('click', this.onClickCapture, true);
      this.list.addEventListener('dragstart', this.onDragBlock);
    }
    if (accessibility) this.el.addEventListener('keydown', this.onKeyDown);
    if (focusOnSelect) this.list.addEventListener('click', this.onSlideClick);
  }

  // Slides are often <a> (card-product). Without this, a swipe navigates.
  onClickCapture(event) {
    if (!this.dragged) return;
    this.dragged = false;
    event.preventDefault();
    event.stopPropagation();
  }

  onSlideClick(event) {
    const node = event.target.closest('.slick-slide');
    if (!node || node.classList.contains('slick-cloned')) return;
    const index = Number(node.dataset.slickIndex);
    if (!Number.isNaN(index)) this.goTo(index);
  }

  onKeyDown(event) {
    if (event.target.matches('input, textarea, select')) return;
    const { rtl } = this.options;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.interrupted = true;
      rtl ? this.slideNext() : this.slidePrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.interrupted = true;
      rtl ? this.slidePrev() : this.slideNext();
    }
  }

  onDragStart(event) {
    if (event.button > 0) return;
    if (event.pointerType === 'mouse' && !this.options.draggable) return;
    if (event.pointerType !== 'mouse' && !this.options.swipe) return;

    // Without this the browser starts a native text/image drag, which swallows
    // pointerup and leaves the slider stuck to the cursor.
    if (event.pointerType === 'mouse') event.preventDefault();

    const { vertical, verticalSwiping } = this.options;
    const useY = vertical || verticalSwiping;

    this.touch = {
      start: useY ? event.clientY : event.clientX,
      startPos: this.getLeft(this.currentSlide),
      moved: 0,
    };

    // Bound on window so a release anywhere on the page still ends the drag.
    window.addEventListener('pointermove', this.onDragMove);
    window.addEventListener('pointerup', this.onDragEnd);
    window.addEventListener('pointercancel', this.onDragEnd);
    window.addEventListener('blur', this.onDragEnd);
  }

  onDragMove(event) {
    if (!this.touch || this.options.fade) return;

    const { vertical, verticalSwiping, infinite, edgeFriction, touchThreshold } = this.options;
    const useY = vertical || verticalSwiping;
    const delta = (useY ? event.clientY : event.clientX) - this.touch.start;

    this.touch.moved = delta;
    if (Math.abs(delta) < touchThreshold) return;

    this.dragged = true;
    this.list.classList.add('dragging');

    let applied = delta;
    if (!infinite) {
      const atStart = this.currentSlide <= 0 && delta > 0;
      const atEnd = this.currentSlide >= this.slideCount - this.options.slidesToShow && delta < 0;
      if (atStart || atEnd) applied = delta * edgeFriction;
    }

    this.setCSS(this.touch.startPos + applied);
  }

  onDragEnd() {
    if (!this.touch) return;
    const { moved } = this.touch;
    this.touch = null;
    this.list.classList.remove('dragging');
    this.releaseDrag();

    if (this.options.fade) return;

    const unit = (this.options.vertical ? this.slideHeight : this.slideWidth) || 1;
    if (Math.abs(moved) < unit / 4) {
      this.setCSS(this.getLeft(this.currentSlide), true);
      return;
    }

    this.emit('swipe', { direction: moved < 0 ? 'left' : 'right' });
    this.interrupted = true;

    if (this.options.swipeToSlide) {
      const steps = Math.round(Math.abs(moved) / unit) || 1;
      this.goTo(this.currentSlide + (moved < 0 ? steps : -steps));
      return;
    }

    if (moved < 0) this.slideNext();
    else this.slidePrev();
  }

  /* ---------- autoplay ---------- */

  releaseDrag() {
    window.removeEventListener('pointermove', this.onDragMove);
    window.removeEventListener('pointerup', this.onDragEnd);
    window.removeEventListener('pointercancel', this.onDragEnd);
    window.removeEventListener('blur', this.onDragEnd);
  }

  onDragBlock(event) {
    event.preventDefault();
  }

  startAutoplay() {
    if (!this.options.autoplay) return;
    this.stopAutoplay();
    this.paused = false;
    this.autoplayTimer = window.setInterval(() => {
      if (!this.paused) this.slideNext();
    }, this.options.autoplaySpeed);
    this.bindAutoplayPause();
  }

  bindAutoplayPause() {
    if (this.autoplayBound) return;
    this.autoplayBound = true;
    const { pauseOnHover, pauseOnFocus, pauseOnDotsHover } = this.options;
    const pause = () => { this.paused = true; };
    const play = () => { this.paused = false; };

    if (pauseOnHover) {
      this.el.addEventListener('mouseenter', pause);
      this.el.addEventListener('mouseleave', play);
    }
    if (pauseOnFocus) {
      this.el.addEventListener('focusin', pause);
      this.el.addEventListener('focusout', play);
    }
    if (pauseOnDotsHover && this.dots) {
      this.dots.addEventListener('mouseenter', pause);
      this.dots.addEventListener('mouseleave', play);
    }
  }

  stopAutoplay() {
    if (this.autoplayTimer) window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  /* ---------- accessibility ---------- */

  initADA() {
    if (!this.options.accessibility) return;

    for (const node of this.track.children) node.setAttribute('tabindex', '-1');

    this.slides.forEach((node, i) => {
      node.setAttribute('role', 'tabpanel');
      node.setAttribute('id', `slick-slide${this.instanceUid}${i}`);
      const dot = this.dots?.children[Math.floor(i / this.options.slidesToScroll)];
      const control = dot?.querySelector('button');
      if (control) node.setAttribute('aria-describedby', control.id);
    });

    if (this.options.focusOnChange) {
      for (const node of this.slides) node.setAttribute('tabindex', '0');
    }
  }

  /* ---------- lifecycle ---------- */

  onResize() {
    if (this.checkResponsive()) return;
    if (!this.unslicked) this.setPosition();
  }

  // A slider initialised while hidden measures 0 and its slides collapse.
  // Recompute as soon as the element actually gains width.
  observeSize() {
    if (typeof ResizeObserver === 'undefined') return;
    this.lastWidth = this.el.offsetWidth;
    this.sizeObserver = new ResizeObserver(() => {
      const width = this.el.offsetWidth;
      if (width === this.lastWidth || this.unslicked) return;
      this.lastWidth = width;
      if (width > 0) this.setPosition();
    });
    this.sizeObserver.observe(this.el);
  }

  refresh(preserveIndex = true) {
    if (this.unslicked) return;
    const index = preserveIndex ? Math.min(this.currentSlide, Math.max(this.slideCount - 1, 0)) : 0;
    this.teardown();
    this.currentSlide = index;
    this.init();
  }

  teardown() {
    this.stopAutoplay();
    window.clearTimeout(this.settleTimer);
    cancelAnimationFrame(this.rafId);
    this.animating = false;
    this.dragged = false;
    this.autoplayBound = false;

    this.releaseDrag();
    this.touch = null;
    this.list?.removeEventListener('pointerdown', this.onDragStart);
    this.list?.removeEventListener('click', this.onClickCapture, true);
    this.list?.removeEventListener('dragstart', this.onDragBlock);
    this.list?.removeEventListener('click', this.onSlideClick);
    this.el.removeEventListener('keydown', this.onKeyDown);

    for (const arrow of [this.prevArrow, this.nextArrow]) arrow?.remove();
    this.dots?.remove();

    this.el.innerHTML = this.originalHTML;
    this.el.classList.remove('slick-slider', 'slick-initialized', 'slick-dotted', 'slick-vertical');
    this.list = null;
    this.track = null;
    this.dots = null;
    this.prevArrow = null;
    this.nextArrow = null;
    this.rowsApplied = false;
  }

  unslick(breakpoint = null) {
    this.emit('unslick', { breakpoint });
    this.teardown();
    this.unslicked = true;
  }

  destroy() {
    window.removeEventListener('resize', this.onResize);
    this.sizeObserver?.disconnect();
    this.sizeObserver = null;
    this.teardown();
    this.emit('destroy', {});
    CometSlider.instances.delete(this);
    delete this.el.slick;
  }

  emit(name, detail) {
    this.el.dispatchEvent(new CustomEvent(`slider:${name}`, {
      bubbles: true,
      detail: { slick: this, slideCount: this.slideCount, ...detail },
    }));
  }

  /* ---------- public API (Slick method parity) ---------- */

  getSlick() { return this; }

  slickGoTo(index, dontAnimate = false) { this.goTo(index, !dontAnimate); }

  slickNext() { this.slideNext(); }

  slickPrev() { this.slidePrev(); }

  slickPause() { this.paused = true; this.stopAutoplay(); }

  slickPlay() { this.options.autoplay = true; this.startAutoplay(); }

  slickCurrentSlide() { return this.currentSlide; }

  slickGetOption(key) { return this.options[key]; }

  slickSetOption(key, value, refresh = false) {
    if (key && typeof key === 'object') Object.assign(this.originalSettings, key);
    else this.originalSettings[key] = value;
    this.activeBreakpoint = null;
    this.registerBreakpoints();
    if (!this.checkResponsive(true)) {
      this.options = { ...this.originalSettings };
      if (refresh !== false) this.refresh();
    }
  }

  slickAdd(markup, index, addBefore = false) {
    const node = markup instanceof Element ? markup : fromMarkup(markup);
    if (!node) return;
    const source = this.el.querySelector('.slick-list') ? this.slides : [...this.el.children];
    const at = index === undefined ? (addBefore ? 0 : source.length) : index;
    const html = this.originalHTML;
    const holder = document.createElement('div');
    holder.innerHTML = html;
    const ref = holder.children[at] ?? null;
    holder.insertBefore(node, addBefore ? ref : ref?.nextSibling ?? null);
    this.originalHTML = holder.innerHTML;
    this.refresh();
  }

  slickRemove(index, removeBefore = false) {
    const holder = document.createElement('div');
    holder.innerHTML = this.originalHTML;
    const at = index === undefined
      ? (removeBefore ? 0 : holder.children.length - 1)
      : (removeBefore ? index - 1 : index);
    holder.children[at]?.remove();
    this.originalHTML = holder.innerHTML;
    this.refresh();
  }

  slickFilter(selector) {
    this.unfilteredHTML ??= this.originalHTML;
    const holder = document.createElement('div');
    holder.innerHTML = this.unfilteredHTML;
    const keep = [...holder.children].filter((n) => (
      typeof selector === 'function' ? selector(n) : n.matches(selector)
    ));
    holder.innerHTML = '';
    for (const n of keep) holder.appendChild(n);
    this.originalHTML = holder.innerHTML;
    this.currentSlide = 0;
    this.refresh(false);
  }

  slickUnfilter() {
    if (!this.unfilteredHTML) return;
    this.originalHTML = this.unfilteredHTML;
    this.unfilteredHTML = null;
    this.currentSlide = 0;
    this.refresh(false);
  }

  /* ---------- static ---------- */

  static instances = new Set();

  static init(target, options = {}) {
    return toNodes(target).map((node) => (
      node.slick ?? new CometSlider(node, typeof options === 'function' ? options(node) : options)
    ));
  }

  static get(target) {
    return toNodes(target)[0]?.slick ?? null;
  }
}

window.CometSlider = CometSlider;
